import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { getDocumentPanelPath } from '../utils/document-panel-paths';
import { ProjectPanelLayout } from './ProjectPanelLayout';
import { ProjectSectionHub } from './ProjectSectionHub';
import { SectionHubCard, type SectionHubItem } from './SectionHubCard';

const RECENT_LIMIT = 5;

interface TitledEntity {
  id: string;
  title?: string;
  name?: string;
  order?: number;
}

export function extractChapterEntities(chaptersData: {
  chaptersByLevel?: Record<string, TitledEntity[]>;
}): TitledEntity[] {
  if (!chaptersData.chaptersByLevel || typeof chaptersData.chaptersByLevel !== 'object') {
    return [];
  }
  const byLevel = chaptersData.chaptersByLevel;
  const levelKeys = Object.keys(byLevel).sort((a, b) => Number(a) - Number(b));
  return levelKeys.flatMap((key) => byLevel[key] || []);
}

function toHubItems(
  entities: TitledEntity[],
  sectionHref: string,
  fallbackLabel: string,
  itemQueryParam: string
): SectionHubItem[] {
  return entities.slice(0, RECENT_LIMIT).map((entity, index) => {
    const id = entity.id || entity.title || entity.name || `item-${index}`;
    const href =
      entity.id && sectionHref
        ? `${sectionHref}?${itemQueryParam}=${encodeURIComponent(entity.id)}`
        : sectionHref;
    return {
      id,
      label: entity.title || entity.name || fallbackLabel,
      href,
    };
  });
}

export const StructureLanding: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { token } = useAuth();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [mainDocumentId, setMainDocumentId] = useState<string | null>(null);
  const [storylineEntities, setStorylineEntities] = useState<TitledEntity[]>([]);
  const [sceneEntities, setSceneEntities] = useState<TitledEntity[]>([]);
  const [chapterEntities, setChapterEntities] = useState<TitledEntity[]>([]);

  useEffect(() => {
    if (!projectId || !token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      try {
        const projectRes = await fetch(`${API_BASE}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const projectData = projectRes.ok ? await projectRes.json() : {};
        const nextMainDocumentId =
          typeof projectData.mainDocumentId === 'string' ? projectData.mainDocumentId : null;

        if (!nextMainDocumentId) {
          if (!cancelled) {
            setMainDocumentId(null);
            setStorylineEntities([]);
            setSceneEntities([]);
            setChapterEntities([]);
          }
          return;
        }

        const [storylinesRes, scenesRes, chaptersRes] = await Promise.all([
          fetch(`${API_BASE}/api/documents/${nextMainDocumentId}/storylines`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/api/documents/${nextMainDocumentId}/scenes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/api/documents/${nextMainDocumentId}/chapters`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const storylinesData = storylinesRes.ok ? await storylinesRes.json() : { storylines: [] };
        const scenesData = scenesRes.ok ? await scenesRes.json() : { scenes: [] };
        const chaptersData = chaptersRes.ok ? await chaptersRes.json() : {};

        if (cancelled) {
          return;
        }

        setMainDocumentId(nextMainDocumentId);
        setStorylineEntities((storylinesData.storylines || []) as TitledEntity[]);
        setSceneEntities(
          ((scenesData.scenes || []) as TitledEntity[])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
        setChapterEntities(extractChapterEntities(chaptersData));
      } catch {
        if (!cancelled) {
          setMainDocumentId(null);
          setStorylineEntities([]);
          setSceneEntities([]);
          setChapterEntities([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, token]);

  const storylinesHref =
    projectId && mainDocumentId
      ? getDocumentPanelPath(projectId, mainDocumentId, 'storylines')
      : '';
  const scenesHref =
    projectId && mainDocumentId ? getDocumentPanelPath(projectId, mainDocumentId, 'scenes') : '';
  const chaptersHref =
    projectId && mainDocumentId ? getDocumentPanelPath(projectId, mainDocumentId, 'chapters') : '';
  const analysisHref =
    projectId && mainDocumentId ? getDocumentPanelPath(projectId, mainDocumentId, 'analysis') : '';

  const untitled = t('untitled');
  const storylines = toHubItems(storylineEntities, storylinesHref, untitled, 'storylineId');
  const scenes = toHubItems(sceneEntities, scenesHref, untitled, 'sceneId');
  const chapters = toHubItems(chapterEntities, chaptersHref, untitled, 'chapterId');
  const analysisItems = toHubItems(chapterEntities, analysisHref, untitled, 'chapterId');

  if (!projectId) {
    return null;
  }

  if (!loading && mainDocumentId == null) {
    return (
      <ProjectPanelLayout title={t('Structure')} titleIcon={PROJECT_SECTION_ICONS.structure}>
        <p className="max-w-2xl text-sm text-muted-foreground">
          {t('section_hub_needs_main_document')}
        </p>
      </ProjectPanelLayout>
    );
  }

  return (
    <ProjectSectionHub
      title={t('Structure')}
      titleIcon={PROJECT_SECTION_ICONS.structure}
      intro={t('structure_landing_intro')}
      loading={loading}
    >
      <SectionHubCard
        title={t('storylines')}
        icon={PROJECT_SECTION_ICONS.storylines}
        href={storylinesHref}
        emptyDescription={t('storylines_empty_description')}
        items={storylines}
        itemsHeading={t('section_hub_listing_storylines')}
      />
      <SectionHubCard
        title={t('scenes')}
        icon={PROJECT_SECTION_ICONS.scenes}
        href={scenesHref}
        emptyDescription={t('scenes_empty_description')}
        items={scenes}
        itemsHeading={t('section_hub_listing_scenes')}
      />
      <SectionHubCard
        title={t('chapters')}
        icon={PROJECT_SECTION_ICONS.chapters}
        href={chaptersHref}
        emptyDescription={t('chapters_empty_description')}
        items={chapters}
        itemsHeading={t('section_hub_listing_chapters')}
      />
      <SectionHubCard
        title={t('Analysis')}
        icon={PROJECT_SECTION_ICONS.analysis}
        href={analysisHref}
        emptyDescription={t('analysis_empty_description')}
        items={analysisItems}
        itemsHeading={t('section_hub_listing_analysis')}
      />
    </ProjectSectionHub>
  );
};
